import React, { useState, useEffect, useRef, useCallback } from "react";
import {
    Box, Heading, VStack, HStack, Input, Select, Button, Text, Flex, Progress, useToast, Checkbox, CheckboxGroup, SimpleGrid, Icon, Alert, AlertIcon, AlertTitle, AlertDescription, Link, Stack
} from '@chakra-ui/react';
import { FaFileCsv, FaCheckCircle, FaExclamationTriangle, FaDownload } from 'react-icons/fa';
import { startProductExport, getExportStatus, downloadExportFile } from "../services/api";

const ALL_COLUMNS = [
    'ID', 'NOME', 'CODIGO GLOBAL', 'DESCRICAO', 'COR', 'MODELO',
    'ALTURA', 'LARGURA', 'COMPRIMENTO', 'PESO', 'DIMENSOES ATUALIZADAS',
    'BLOQUEADO', 'BLOQUEIO TEMPORARIO',
    'CODIGO BARRA', 'FORNECEDOR', 'CODIGO FORNECEDOR', 'MARCA', 'FABRICANTE', 'CODIGO FABRICANTE', 'NCM',
    'QUANTIDADE', 'QUANTIDADE TAGA', 'QUANTIDADE EMBALAGEM',
    'VALOR COMPRA', 'VALOR VENDA',
    'ID ML 1', 'ID ML 2', 'ID ML 3'
];

const FORNECEDORES = [
    { id: 1, nome: 'MORELATE' },
    { id: 2, nome: 'VANNUCCI' },
    { id: 3, nome: 'ISAPA' },
    { id: 4, nome: 'BRCOMPANY' },
    { id: 5, nome: 'AUTOLATINA' },
    { id: 6, nome: 'LNG' },
    { id: 7, nome: 'CBA' },
    { id: 8, nome: 'MSAM' },
    { id: 9, nome: 'BLPARTS' },
];

const ALL_FORNECEDOR_IDS = FORNECEDORES.map(f => f.id.toString());

const ExportarProdutos = () => {
    const [selectedColumns, setSelectedColumns] = useState(ALL_COLUMNS);
    const [filters, setFilters] = useState({
        nome: '',
        codigo_global: '',
        fornecedor_id: ALL_FORNECEDOR_IDS,
        fabricante: '',
        estoque_taga: false
    });
    const [jobId, setJobId] = useState(null);
    const [jobStatus, setJobStatus] = useState(null);
    const [isGenerating, setIsGenerating] = useState(false);

    const toast = useToast();
    const intervalRef = useRef(null);

    const handleFilterChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFilters(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleFornecedorChange = (selectedIds) => {
        setFilters(prev => ({
            ...prev,
            fornecedor_id: selectedIds
        }));
    };

    const handleSelectAllColumns = (e) => {
        if (e.target.checked) {
            setSelectedColumns(ALL_COLUMNS);
        } else {
            setSelectedColumns([]);
        }
    };

    const handleStartExport = async () => {
        if (selectedColumns.length === 0) {
            toast({
                title: "Nenhuma coluna selecionada",
                description: "Por favor, selecione pelo menos uma coluna para exportar.",
                status: "warning",
                duration: 5000,
                isClosable: true,
            });
            return;
        }

        setIsGenerating(true);
        setJobStatus(null);
        setJobId(null);

        try {
            const data = await startProductExport(selectedColumns, filters);
            setJobId(data.job_id);
            toast({
                title: "Exportação iniciada!",
                description: "O arquivo está sendo gerado em segundo plano. Você será notificado quando estiver pronto.",
                status: "info",
                duration: 5000,
                isClosable: true,
            });
        } catch (error) {
            toast({
                title: "Erro ao iniciar a exportação",
                description: error.response?.data?.errors ? JSON.stringify(error.response.data.errors) : (error.response?.data?.message || "Ocorreu um erro inesperado."),
                status: "error",
                duration: 9000,
                isClosable: true,
            });
            setIsGenerating(false);
        }
    };

    const fetchStatus = useCallback(async () => {
        if (!jobId) return;

        try {
            const statusData = await getExportStatus(jobId);
            setJobStatus(statusData);

            if (['completed', 'failed'].includes(statusData.status)) {
                setIsGenerating(false);
                if (intervalRef.current) {
                    clearInterval(intervalRef.current);
                    intervalRef.current = null;
                }
                if (statusData.status === 'completed') {
                    toast({
                        title: "Arquivo Pronto!",
                        description: "Sua exportação de produtos está pronta para download.",
                        status: "success",
                        duration: 7000,
                        isClosable: true,
                    });
                }
            }
        } catch (error) {
            console.error("Erro ao buscar status:", error);
            setIsGenerating(false);
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
                intervalRef.current = null;
            }
        }
    }, [jobId, toast]);

    useEffect(() => {
        if (isGenerating && jobId) {
            if (!intervalRef.current) {
                intervalRef.current = setInterval(fetchStatus, 5000);
            }
        }
        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
    }, [isGenerating, jobId, fetchStatus]);


    const handleDownload = async () => {
        if (jobStatus?.file_name) {
            try {
                await downloadExportFile(jobStatus.file_name);

                toast({
                    title: "Download Iniciado",
                    status: "success",
                    duration: 3000,
                    isClosable: true,
                });

                setJobStatus(null);
                setJobId(null);

            } catch (error) {
                toast({
                    title: "Erro no Download",
                    description: "Não foi possível baixar o arquivo. Por favor, gere um novo relatório.",
                    status: "error",
                    duration: 5000,
                    isClosable: true,
                });

                setJobStatus(null);
                setJobId(null);
            }
        }
    }

    const renderStatus = () => {
        if (!jobStatus) return null;
        switch (jobStatus.status) {
            case 'pending':
            case 'running':
                return (
                    <Box w="100%">
                        <Flex justify="space-between" mb={1}>
                            <Text fontSize="sm" fontWeight="medium">{jobStatus.message}</Text>
                            <Text fontSize="sm" fontWeight="bold" color="blue.600">{jobStatus.progress || 0}%</Text>
                        </Flex>
                        <Progress value={jobStatus.progress} size="md" colorScheme="blue" borderRadius="md" isIndeterminate={jobStatus.status === 'pending'} />
                    </Box>
                );
            case 'completed':
                return (
                    <Alert status="success" borderRadius="md" variant="left-accent">
                        <AlertIcon as={FaCheckCircle} />
                        <Box flex="1">
                            <AlertTitle>Arquivo Pronto!</AlertTitle>
                            <AlertDescription>
                                <Button leftIcon={<FaDownload />} colorScheme="green" variant="solid" onClick={handleDownload} size="sm">
                                    Baixar Planilha
                                </Button>
                            </AlertDescription>
                        </Box>
                    </Alert>
                );
            case 'failed':
                return (
                    <Alert status="error" borderRadius="md" variant="left-accent">
                        <AlertIcon as={FaExclamationTriangle} />
                        <Box flex="1">
                            <AlertTitle>Falha na Exportação!</AlertTitle>
                            <AlertDescription fontSize="sm">{jobStatus.message || 'Ocorreu um erro inesperado.'}</AlertDescription>
                        </Box>
                    </Alert>
                );
            default: return null;
        }
    };

    return (
        <Box ml={{ base: 0, lg: '60px' }} p={{ base: 4, md: 8 }} bg="gray.50" minH="100vh">
            <VStack spacing={6} align="stretch" maxW="5xl" mx="auto">
                <Heading justify="center" color={"#F68633"} size="lg">Exportar Base de Produtos</Heading>

                <Box p={6} bg="white" borderRadius="lg" borderWidth="1px" w="100%" boxShadow="sm">
                    <Heading size="md" mb={4}>1. Selecione as Colunas</Heading>
                    <Checkbox isChecked={selectedColumns.length === ALL_COLUMNS.length} onChange={handleSelectAllColumns} mb={4} fontWeight="bold">
                        Selecionar Todas as Colunas
                    </Checkbox>
                    <CheckboxGroup colorScheme="orange" value={selectedColumns} onChange={setSelectedColumns}>
                        <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={4}>
                            {ALL_COLUMNS.map(col => <Checkbox key={col} value={col}>{col}</Checkbox>)}
                        </SimpleGrid>
                    </CheckboxGroup>
                </Box>

                <Box p={6} bg="white" borderRadius="lg" borderWidth="1px" w="100%" boxShadow="sm">
                    <Heading size="md" mb={4}>2. Aplique Filtros (Opcional)</Heading>
                    <Stack spacing={4}>
                        <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
                        </SimpleGrid>

                        <Box>
                            <Heading size="sm" mb={3}>Fornecedores</Heading>
                            <Checkbox
                                colorScheme="orange"
                                isChecked={filters.fornecedor_id.length === FORNECEDORES.length}
                                onChange={(e) => handleFornecedorChange(e.target.checked ? ALL_FORNECEDOR_IDS : [])}
                                mb={3}
                                fontWeight="bold"
                            >
                                Selecionar Todos Fornecedores
                            </Checkbox>
                            <CheckboxGroup colorScheme="orange" value={filters.fornecedor_id} onChange={handleFornecedorChange}>
                                <SimpleGrid columns={{ base: 2, md: 3 }} spacing={4}>
                                    {FORNECEDORES.map(f => (
                                        <Checkbox key={f.id} value={f.id.toString()}>{f.nome}</Checkbox>
                                    ))}
                                </SimpleGrid>
                            </CheckboxGroup>
                        </Box>

                        <Checkbox name="estoque_taga" isChecked={filters.estoque_taga} onChange={handleFilterChange}>
                            Apenas com Estoque TAGA
                        </Checkbox>
                    </Stack>
                </Box>

                <Box p={6} bg="white" borderRadius="lg" borderWidth="1px" w="100%" boxShadow="sm">
                    <Heading size="md" mb={4}>3. Gere o Arquivo</Heading>
                    <VStack spacing={4}>
                        Gerar Arquivo de Exportação
                        <Button leftIcon={<Icon as={FaFileCsv} />} colorScheme="orange" onClick={handleStartExport} isLoading={isGenerating} loadingText="Gerando..." isDisabled={isGenerating} w="full" size="lg">
                            Gerar Arquivo de Exportação
                        </Button>
                        {renderStatus()}
                    </VStack>
                </Box>
            </VStack>
        </Box>
    );
};

export default ExportarProdutos;